import { useState, useCallback, useRef } from 'react';

type MutationState<T, E> = {
	isPending: boolean;
	isError: boolean;
	isSuccess: boolean;
	error: E | undefined;
	data: T | undefined;
};

type MutationOptions<T, E, V> = {
	onError?: (error: E, variables: V) => void;
	onSuccess?: (data: T, variables: V) => void;
	onSettled?: (data?: T, error?: E, variables?: V) => void;
};

// fn is a function that takes in variables of type V and returns a Promise of T
export default function useMutation<T = any, E = any, V = any>(
	fn: (variables: V, signal?: AbortSignal) => Promise<T>, // Add signal as optional param
	options?: MutationOptions<T, E, V>,
) {
	const [state, setState] = useState<MutationState<T, E>>({
		isPending: false,
		isError: false,
		isSuccess: false,
		error: undefined,
		data: undefined,
	});

	const abortControllerRef = useRef<AbortController | null>(null);

	const mutateAsync = useCallback(
		async (variables: V) => {
			// If there's already an active request, abort it before starting a new one
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create a new AbortController for this request
			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			setState({
				isPending: true,
				isError: false,
				isSuccess: false,
				error: undefined,
				data: undefined,
			});

			try {
				const data = await fn(variables, abortController.signal); // Pass the abort signal

				setState({
					isPending: false,
					isError: false,
					isSuccess: true,
					error: undefined,
					data,
				});

				if (options?.onSuccess) {
					options.onSuccess(data, variables);
				}

				return data;
			} catch (error) {
				if (abortController.signal.aborted) {
					// Handle aborted request if needed
					console.log('Request aborted');
				} else {
					setState({
						isPending: false,
						isError: true,
						isSuccess: false,
						error: error as E,
						data: undefined,
					});

					if (options?.onError) {
						options.onError(error as E, variables);
					}
				}

				throw error;
			} finally {
				if (options?.onSettled) {
					options.onSettled(state.data, state.error, variables);
				}

				// Clean up AbortController after request is settled
				abortControllerRef.current = null;
			}
		},
		[fn, options],
	);

	const abort = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
	}, []);

	return {
		...state,
		mutateAsync, // Updated function name
		abort, // Allow the mutation to be aborted manually if needed
	};
}
